import React, { ReactElement } from "react";
import Email from "../../Email/Email";
import { Grid } from "@mui/material";
import {
  AccountBalanceOutlined,
  AssignmentTurnedInOutlined,
  ChangeCircleOutlined,
  Close,
  Create,
  EmailOutlined,
  HourglassEmptyOutlined,
  Launch,
  NoteOutlined,
  OpenInBrowser,
  PersonAdd,
  PrintOutlined,
  RotateLeft,
  SendOutlined,
} from "@mui/icons-material";
import EmojiEvents from "../../../assets/icon/emoji_events-24px.svg";
import CircleIcon from "../../CircleIcon/CircleIcon";
import CaseHistoryActivity from "./CustomCaseHistoryItem/CaseHistoryActivity";
import Note from "../CaseNote/Note";
import { translate } from "../../../services/Translations/translatorService";
import PaidTaskHistoryLog from "./CustomCaseHistoryItem/PaidTaskHistoryLog";
import CaseHistoryMediaObject from "./CustomCaseHistoryItem/CaseHistoryMediaObject";
import CaseHistoryInquiryToCustomer from "./CustomCaseHistoryItem/CaseHistoryInquiryToCustomer";
import { CurrentAndPreviousState, HistoryLog } from "../../../types/HistoryLog";
import caseHistoryItemStyle from "./caseHistoryItemStyle";
import GenericLogEntry from "./GenericLogEntry";
import CaseHistoryMessage from "./CustomCaseHistoryItem/CaseHistoryMessage";
import CaseHistoryInvoiceTypeIdentifierChange from "./CustomCaseHistoryItem/CaseHistoryInvoiceTypeIdentifierChange";
import CaseHistoryInvoiceDelete from "./CustomCaseHistoryItem/CaseHistoryInvoiceDelete";
import CaseHistoryInvoiceReassign from "./CustomCaseHistoryItem/CaseHistoryInvoiceReassign";
import CaseHistoryLeadStatusChange from "./CustomCaseHistoryItem/CaseHistoryLeadStatusChange";
import { cloneHistoryLogAsGeneric } from "../../../services/historyService";
import CaseHistoryLeadStatusChangeByCustomerPrevented from "./CustomCaseHistoryItem/CaseHistoryLeadStatusChangeByCustomerPrevented";
import { stripHtml } from "string-strip-html";
import { ProductUnion } from "../../../types/ProductUnion";

interface CaseHistoryItemProps {
  historyItem: HistoryLog;
  fetchHistory: Function;
  product: ProductUnion;
}

const CaseHistoryItem = ({ historyItem, fetchHistory, product }: CaseHistoryItemProps): null | ReactElement => {
  const { productClassName } = product;

  switch (historyItem.type) {
    // GENERIC LOG ENTRY DISPLAY COMPONENTS
    case "customerRegistration":
      return (
        <GenericLogEntry
          subject={translate("historyItem.customerRegistration.label")}
          Icon={PersonAdd}
          historyItem={historyItem}
        />
      );
    case "caseCreated":
      return (
        <GenericLogEntry subject={translate("historyItem.caseCreated.label")} Icon={Launch} historyItem={historyItem} />
      );
    case "entityStatusChange":
      return <CaseHistoryLeadStatusChange historyItem={historyItem} productClassName={productClassName} />;
    case "entityStatusChangeByCustomerPrevented":
      return (
        <CaseHistoryLeadStatusChangeByCustomerPrevented historyItem={historyItem} productClassName={productClassName} />
      );
    case "assignedUserChange":
      return (
        <GenericLogEntry
          subject={translate("historyItem.assignedUserChange.label")}
          Icon={Create}
          info={["lawyer", "accountManager"].map((key: string) => {
            if (!(key in historyItem.logData)) return null;
            return (
              <div key={key}>
                {translate(`historyItem.${key}.label`)}:{" "}
                {historyItem.logData[key].previous + " >> " + historyItem.logData[key].current}
              </div>
            );
          })}
          historyItem={historyItem}
        />
      );
    case "customerDataChange":
      return (
        <GenericLogEntry
          subject={translate("historyItem.customerDataChange.label")}
          Icon={Create}
          info={Object.entries(historyItem.logData).map(
            ([key, logItem]: [key: string, logItem: CurrentAndPreviousState]) => {
              return (
                <div key={key}>
                  {translate(`customer.${key}.label`)}:
                  {logItem.previous
                    ? translate(`customer.${key}.values.${logItem.previous}`, String(logItem.previous))
                    : "Leer "}
                  {" >> "}
                  {logItem.current
                    ? translate(`customer.${key}.values.${logItem.current}`, String(logItem.current))
                    : "Leer"}
                </div>
              );
            }
          )}
          historyItem={historyItem}
        />
      );
    case "entityDataChange":
      return (
        <GenericLogEntry
          subject={translate("historyItem.entityDataChange.label")}
          Icon={Create}
          info={Object.entries(historyItem.logData).map(([key, value]: [key: string, value: any]) => {
            return (
              <div key={key}>
                <Grid container>
                  <Grid>{translate(`${productClassName}.${key}.label`)}:&nbsp;</Grid>
                  <Grid>
                    {
                      stripHtml(
                        !!value.current || value.current === false
                          ? translate(`${productClassName}.${key}.values.${value.previous}`, String(value.previous))
                          : "Leer"
                      ).result
                    }
                  </Grid>
                  <Grid>&nbsp;{">>"}&nbsp;</Grid>
                  <Grid>
                    {
                      stripHtml(
                        !!value.current || value.current === false
                          ? translate(`${productClassName}.${key}.values.${value.current}`, String(value.current))
                          : "Leer"
                      ).result
                    }
                  </Grid>
                </Grid>
              </div>
            );
          })}
          historyItem={historyItem}
        />
      );
    case "fax":
      return historyItem.logData.fax ? (
        <GenericLogEntry
          subject={`Fax (${historyItem.logData.fax.sessionId}) an +49${
            historyItem.logData.fax.targetNumber
          } ${translate("fax.status.values." + historyItem.logData.fax.status, "")}`}
          Icon={PrintOutlined}
          historyItem={historyItem}
        />
      ) : null;
    case "beaMessage":
      return (
        <GenericLogEntry
          subject={`beA ${translate(
            "beaMessage.transmissionDirection.values." + historyItem.logData.beaMessage.transmissionDirection,
            ""
          )}: ${historyItem.logData.beaMessage.beaMessageId} (${historyItem.logData.beaMessage.subject}) an ${
            historyItem.logData.recipientName
          }, ${historyItem.logData.beaMessage.recipient.safeId}`}
          Icon={SendOutlined}
          historyItem={cloneHistoryLogAsGeneric(historyItem)}
        />
      );
    case "beaOrFaxPause":
      return (
        <GenericLogEntry
          subject={`Automatische beA/Faxe ${
            historyItem.logData.preventAllSystemBeaOrFax ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={SendOutlined}
          historyItem={historyItem}
        />
      );
    case "passwordReset":
      return (
        <GenericLogEntry subject={"Passwort-Setzen-E-Mail versendet"} Icon={EmailOutlined} historyItem={historyItem} />
      );
    //this is here historically, functionality was removed
    case "activitiesPause":
      return (
        <GenericLogEntry
          subject={`Setzen von Aktivitäten ${
            historyItem.logData.preventAllSystemActivities ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={AssignmentTurnedInOutlined}
          historyItem={historyItem}
        />
      );
    case "caseActivityCheck":
      return (
        <GenericLogEntry
          subject={`Automatischer Statuscheck ${
            historyItem.logData.shouldCheckCaseActivity
              ? `aktiviert - Häufigkeit: ${historyItem.logData.checkCaseActivityIntervalInMonths?.current} Monat${
                  historyItem.logData.checkCaseActivityIntervalInMonths?.current === 1 ? "" : "e"
                }`
              : "deaktiviert"
          }`}
          Icon={RotateLeft}
          historyItem={historyItem}
        />
      );
    case "caseStatusChange":
      switch (historyItem.logData.caseStatus?.current) {
        case "inactive":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.inactive.label")}
              Icon={HourglassEmptyOutlined}
              historyItem={historyItem}
            />
          );
        case "lost":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.lost.label")}
              Icon={Close}
              info={
                historyItem.logData.caseStatus?.current === "lost" && (
                  <>
                    {historyItem.logData?.lostCategory?.current && (
                      <div>
                        Verlustkategorie:{" "}
                        {translate(
                          "historyItem.caseStatusChange.lost.lostCategory." + historyItem.logData.lostCategory.current
                        )}
                      </div>
                    )}
                    {historyItem.logData?.lostReason?.current && (
                      <div>Verlustgrund: {historyItem.logData.lostReason.current}</div>
                    )}
                  </>
                )
              }
              historyItem={historyItem}
            />
          );
        case "won":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.won.label")}
              Icon={EmojiEvents}
              iconType={"iconFile"}
              historyItem={historyItem}
            />
          );
        case "open":
          return (
            <GenericLogEntry
              subject={translate("historyItem.caseStatusChange.open.label")}
              Icon={OpenInBrowser}
              historyItem={historyItem}
            />
          );
        default:
          return null;
      }
    // HISTORY LOGS EXTRACTED FROM 'entityDataChange' ONES (SPOOFED)
    case "accountingPause":
      return (
        <GenericLogEntry
          subject={`Automatische Konto/Rechnungen ${
            historyItem.logData.preventAccounting.current ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={AccountBalanceOutlined}
          historyItem={historyItem}
        />
      );

    case "mailPause":
      return (
        <GenericLogEntry
          subject={`Automatische Emails und Nachrichten an die Mandantschaft ${
            historyItem.logData.preventAllSystemEmails!.current ? "pausiert" : "wieder angeschaltet"
          }`}
          Icon={EmailOutlined}
          historyItem={historyItem}
        />
      );
    case "invoiceTypeIdentifierChange":
      return <CaseHistoryInvoiceTypeIdentifierChange productClassName={productClassName} historyItem={historyItem} />;
    case "invoiceDelete":
      return <CaseHistoryInvoiceDelete historyItem={historyItem} />;
    case "invoiceReassign":
      return <CaseHistoryInvoiceReassign historyItem={historyItem} />;
    case "inquiryToCustomer":
      return <CaseHistoryInquiryToCustomer historyLog={historyItem} />;
    case "activity":
      return <CaseHistoryActivity productClassName={productClassName} historyItem={historyItem} />;
    case "paidTask":
      return <PaidTaskHistoryLog paidTask={historyItem.paidTask} />;
    case "emailMessage":
      return <Email isNonInteractive={true} emailMessage={historyItem.emailMessage} product={product} />;
    case "message":
      return (
        <CaseHistoryMessage
          message={historyItem.logData.message}
          conversationSubject={historyItem.logData.conversationSubject}
        />
      );
    case "note":
      return (
        <Grid container>
          <Grid sx={caseHistoryItemStyle.imageGridItem}>
            <CircleIcon icon={<NoteOutlined />} type={"muiIcon"} />
          </Grid>
          <Grid sx={caseHistoryItemStyle.noteGridItem}>
            <Note note={historyItem.note} handleChange={fetchHistory} product={product} />
          </Grid>
        </Grid>
      );
    case "mediaObject":
    case "mediaObjectDelete":
    case "mediaObjectIdentifierChange":
      return <CaseHistoryMediaObject historyLog={historyItem} />;
    case "productChange":
      return (
        <GenericLogEntry
          subject={`Produkt wurde gewechselt von "${translate(`${historyItem.logData.oldProductType}.label`)}" auf "
              ${translate(`${historyItem.logData.newProductType}.label`)}"`}
          Icon={ChangeCircleOutlined}
          historyItem={historyItem}
          userNameFallback={translate("historyItem.client")}
        />
      );
    default:
      return null;
  }
};

export default CaseHistoryItem;
